export const FORMULA = {
  NO_RULE: "NO_RULE",
  FORECAST_LINEAR_LAST_6M: "FORECAST_LINEAR_LAST_6M",
  FORECAST_LINEAR_CUSTOM: "FORECAST_LINEAR_CUSTOM",
  FORECAST_ETS_LAST_6M: "FORECAST_ETS_LAST_6M",
  FORECAST_ETS_CUSTOM: "FORECAST_ETS_CUSTOM",
  TREND_LAST_6M: "TREND_LAST_6M",
  TREND_CUSTOM: "TREND_CUSTOM",
  AVG_LAST_6M: "AVG_LAST_6M",
  AVG_CUSTOM: "AVG_CUSTOM",
  GROWTH_LAST_6M: "GROWTH_LAST_6M",
  GROWTH_CUSTOM: "GROWTH_CUSTOM",
  GROWTH_PCT: "GROWTH_PCT",
  GROWTH_FIXED: "GROWTH_FIXED",
  CUSTOM_FIXED: "CUSTOM_FIXED",
} as const;

export type FormulaValue = (typeof FORMULA)[keyof typeof FORMULA];

export const FORMULA_LABELS: Record<FormulaValue, string> = {
  [FORMULA.NO_RULE]: "No rule",
  [FORMULA.FORECAST_LINEAR_LAST_6M]: "Linear forecast (last 6 months)",
  [FORMULA.FORECAST_LINEAR_CUSTOM]: "Linear forecast Custom",
  [FORMULA.FORECAST_ETS_LAST_6M]: "ETS forecast (last 6 months)",
  [FORMULA.FORECAST_ETS_CUSTOM]: "ETS forecast Custom",
  [FORMULA.TREND_LAST_6M]: "Trend (last 6 months)",
  [FORMULA.TREND_CUSTOM]: "Trend Custom",
  [FORMULA.AVG_LAST_6M]: "Average (last 6 months)",
  [FORMULA.AVG_CUSTOM]: "Average Custom",
  [FORMULA.GROWTH_LAST_6M]: "Growth rate (last 6 months)",
  [FORMULA.GROWTH_CUSTOM]: "Growth rate Custom",
  [FORMULA.GROWTH_PCT]: "Fixed growth (multiplier)",
  [FORMULA.GROWTH_FIXED]: "Fixed growth ($)",
  [FORMULA.CUSTOM_FIXED]: "Fixed amount",
};

export const FORMULA_OPTIONS = Object.entries(FORMULA_LABELS).map(
  ([value, label]) => ({ value: value as FormulaValue, label })
);

export const FORMULA_NEEDS_VALUE = new Set<FormulaValue>([
  FORMULA.GROWTH_PCT,
  FORMULA.GROWTH_FIXED,
  FORMULA.CUSTOM_FIXED,
]);

export const FORMULA_NEEDS_DATE_WINDOW = new Set<FormulaValue>([
  FORMULA.FORECAST_LINEAR_CUSTOM,
  FORMULA.FORECAST_ETS_CUSTOM,
  FORMULA.TREND_CUSTOM,
  FORMULA.AVG_CUSTOM,
  FORMULA.GROWTH_CUSTOM,
]);

export const FORMULA_IS_MULTIPLIER = new Set<FormulaValue>([
  FORMULA.GROWTH_PCT,
]);

export const FORMULA_IS_LAST_6M = new Set<FormulaValue>([
  FORMULA.FORECAST_LINEAR_LAST_6M,
  FORMULA.FORECAST_ETS_LAST_6M,
  FORMULA.TREND_LAST_6M,
  FORMULA.AVG_LAST_6M,
  FORMULA.GROWTH_LAST_6M,
]);

export type FormulaExample = {
  actual: string;
  forecasted: string;
};

export type FormulaInfo = {
  description: string;
  howItWorks: string;
  getExample: (value?: string) => FormulaExample;
};

export const FORMULA_INFO: Partial<Record<FormulaValue, FormulaInfo>> = {
  [FORMULA.GROWTH_PCT]: {
    description:
      "Compounds each forecast month from the average of all actuals using a fixed multiplier.",
    howItWorks:
      "Base = average of all actual months. Month 1 = base × multiplier. Month 2 = base × multiplier². Grows exponentially.",
    getExample: (value = "1.1") => {
      const m = Number(value || 1.1);
      const base = 10000;
      return {
        actual: "$10,000 (average of all actuals)",
        forecasted: `Month 1: $${(
          base * m
        ).toLocaleString()}, Month 2: $${Math.round(
          base * m * m
        ).toLocaleString()}, Month 3: $${Math.round(
          base * m ** 3
        ).toLocaleString()}`,
      };
    },
  },
  [FORMULA.GROWTH_FIXED]: {
    description:
      "Adds a fixed dollar increment each month on top of the average of all actuals.",
    howItWorks:
      "Base = average of all actual months. Month 1 = base + $X. Month 2 = base + $2X. Grows linearly.",
    getExample: (value = "500") => {
      const v = Number(value || 500);
      const base = 10000;
      return {
        actual: "$10,000 (average of all actuals)",
        forecasted: `Month 1: $${(base + v).toLocaleString()}, Month 2: $${(
          base +
          v * 2
        ).toLocaleString()}, Month 3: $${(base + v * 3).toLocaleString()}`,
      };
    },
  },
  [FORMULA.CUSTOM_FIXED]: {
    description:
      "Sets every forecast month to the same fixed dollar amount, ignoring actuals.",
    howItWorks:
      "All forecast months = the value you enter, regardless of history.",
    getExample: (value = "5000") => ({
      actual: "Any amount (ignored)",
      forecasted: `$${Number(value || 5000).toLocaleString()} every month`,
    }),
  },
  [FORMULA.GROWTH_LAST_6M]: {
    description:
      "Computes the compound growth rate from the first to last of the past 6 months and projects it forward.",
    howItWorks:
      "Rate = (last month / first month)^(1/5) − 1. Applied as compound growth from the last actual.",
    getExample: () => ({
      actual: "$8,000 (6 months ago) → $10,000 (last month), rate ≈ 4.6%/month",
      forecasted: "Month 1: $10,460, Month 2: $10,941…",
    }),
  },
  [FORMULA.GROWTH_CUSTOM]: {
    description:
      "Computes the compound growth rate over your chosen date range and projects it forward.",
    howItWorks:
      "Same CAGR formula as Growth (last 6M) but over whatever period you select.",
    getExample: () => ({
      actual: "$5,000 → $9,000 over 12 months (rate ≈ 5.1%/month)",
      forecasted: "Applied forward from last actual",
    }),
  },
  [FORMULA.AVG_LAST_6M]: {
    description:
      "Uses the simple average of the last 6 months as a flat forecast for every future month.",
    howItWorks:
      "Forecast = sum of last 6 months ÷ 6. Same value repeated every forecast month.",
    getExample: () => ({
      actual: "$7K, $8K, $9K, $8K, $10K, $9K (last 6 months)",
      forecasted: "$8,500 every month",
    }),
  },
  [FORMULA.AVG_CUSTOM]: {
    description:
      "Uses the simple average of your chosen date range as a flat forecast.",
    howItWorks:
      "Same as Average (last 6M) but you control which historical period to average.",
    getExample: () => ({
      actual: "Selected period averaged $9,500/month",
      forecasted: "$9,500 every month",
    }),
  },
  [FORMULA.TREND_LAST_6M]: {
    description:
      "Fits a straight line through the last 6 months and continues that slope forward.",
    howItWorks:
      "Uses linear regression (best-fit line). Each forecast month steps along that line — so the amount changes each month.",
    getExample: () => ({
      actual: "Last 6 months trending +$400/month",
      forecasted: "Month 1: +$400, Month 2: +$800, Month 3: +$1,200…",
    }),
  },
  [FORMULA.TREND_CUSTOM]: {
    description:
      "Fits a straight line through your chosen date range and continues that slope forward.",
    howItWorks:
      "Same linear regression as Trend (last 6M) but over your selected period.",
    getExample: () => ({
      actual: "Selected period shows +$700/month slope",
      forecasted: "Month 1: +$700, Month 2: +$1,400, Month 3: +$2,100…",
    }),
  },
  [FORMULA.FORECAST_LINEAR_LAST_6M]: {
    description:
      "Statistical linear regression on the last 6 months — same as Trend but labeled for forecasting context.",
    howItWorks:
      "Identical to Trend (last 6M): fits a best-fit line and extrapolates. Each month gets a different projected value.",
    getExample: () => ({
      actual: "Last 6 months fit a line projecting +$500/month",
      forecasted: "Month 1: $10,500, Month 2: $11,000, Month 3: $11,500…",
    }),
  },
  [FORMULA.FORECAST_LINEAR_CUSTOM]: {
    description: "Statistical linear regression over a custom date range.",
    howItWorks:
      "Same as Forecast Linear (last 6M) but you choose the training period.",
    getExample: () => ({
      actual: "Training period shows a $300/month upward slope",
      forecasted: "Forecast continues that trajectory",
    }),
  },
  [FORMULA.FORECAST_ETS_LAST_6M]: {
    description:
      "Exponential smoothing using the last 6 months — weights recent data more heavily than older data.",
    howItWorks:
      "With <24 months of data: simple smoothing. With 24+ months: Holt-Winters captures trend and monthly seasonality.",
    getExample: () => ({
      actual: "Recent months accelerating faster than older months",
      forecasted:
        "ETS responds faster than a simple average; with 24+ months history it also captures seasonal peaks",
    }),
  },
  [FORMULA.FORECAST_ETS_CUSTOM]: {
    description: "Exponential smoothing over a custom date range.",
    howItWorks:
      "Same ETS model as Forecast ETS (last 6M) but trained on your selected period. 24+ months unlocks seasonality detection.",
    getExample: () => ({
      actual: "2 years of selected history with seasonal patterns",
      forecasted:
        "Model detects seasonal peaks and applies them to forecast months",
    }),
  },
};
